/*
 * ExecutionPanel — collapsible console panel at the bottom of the workflow designer.
 * Shows real-time execution log entries with timestamps, status, and duration.
 * When running a batch, shows a document-level summary table instead.
 */

import React, { useState, useEffect, useRef } from 'react';
import './ExecutionPanel.css';

function formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

function computeDuration(startedAt, completedAt) {
  if (!startedAt || !completedAt) return null;
  try {
    const ms = new Date(completedAt) - new Date(startedAt);
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  } catch {
    return null;
  }
}

function batchStatusIcon(status) {
  if (status === 'completed' || status === 'paused') return '\u2713';
  if (status === 'failed') return '\u2715';
  if (status === 'running') return '\u23F3';
  return '\u2014';
}

// ── Batch Summary Sub-component ──
const BatchSummaryView = ({ batchProgress, batchResults, executionStatus }) => {
  const total = batchProgress?.total || 0;
  const completed = batchProgress?.completed || 0;
  const failed = batchProgress?.failed || 0;
  const processed = completed + failed;
  const isRunning = executionStatus === 'running';

  return (
    <div className="wf-batch-view">
      {/* Progress bar */}
      <div className="wf-batch-progress-bar-container">
        <div className="wf-batch-progress-bar">
          <div
            className={`wf-batch-progress-fill ${failed > 0 ? 'has-failures' : ''}`}
            style={{ width: `${total > 0 ? Math.round((processed / total) * 100) : 0}%` }}
          />
        </div>
        <span className="wf-batch-progress-text">
          {isRunning
            ? `Processing ${processed + 1} of ${total}...`
            : `${completed} completed, ${failed} failed of ${total}`
          }
        </span>
      </div>

      {/* Results table */}
      {batchResults.length > 0 && (
        <table className="wf-batch-table">
          <thead>
            <tr>
              <th className="wf-batch-th-num">#</th>
              <th className="wf-batch-th-doc">Document</th>
              <th className="wf-batch-th-status">Status</th>
              <th className="wf-batch-th-error">Error</th>
            </tr>
          </thead>
          <tbody>
            {batchResults.map((r, idx) => (
              <tr key={r.documentId || idx} className={`wf-batch-row ${r.status}`}>
                <td className="wf-batch-td-num">{idx + 1}</td>
                <td className="wf-batch-td-doc" title={r.documentId}>
                  {r.documentId?.substring(0, 8)}...
                </td>
                <td className="wf-batch-td-status">
                  <span className={`wf-batch-status-icon ${r.status}`}>
                    {batchStatusIcon(r.status)}
                  </span>
                  <span>{r.status}</span>
                </td>
                <td className="wf-batch-td-error">{r.error || ''}</td>
              </tr>
            ))}
            {/* Pending rows */}
            {isRunning && processed < total && (
              <tr className="wf-batch-row pending">
                <td className="wf-batch-td-num">{processed + 1}</td>
                <td className="wf-batch-td-doc" colSpan={3}>
                  <span className="wf-batch-status-icon running">{batchStatusIcon('running')}</span>
                  Processing...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Summary row */}
      {!isRunning && total > 0 && (
        <div className="wf-batch-summary-row">
          Total: {total} &nbsp;|&nbsp; Completed: {completed} &nbsp;|&nbsp; Failed: {failed}
        </div>
      )}
    </div>
  );
};

const ExecutionPanel = ({
  visible,
  executionStatus,
  nodeLog,
  errorMessage,
  startedAt,
  completedAt,
  batchProgress,
  batchResults,
  onClose,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const bodyRef = useRef(null);
  const isBatch = batchProgress && batchProgress.total > 1;

  // Auto-scroll to bottom when new log entries arrive
  useEffect(() => {
    if (bodyRef.current && !collapsed) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [nodeLog, batchResults, collapsed]);

  if (!visible) return null;

  const overallDuration = computeDuration(startedAt, completedAt);

  return (
    <div className={`wf-exec-panel ${collapsed ? 'collapsed' : ''}`}>
      <div className="wf-exec-header" onClick={() => setCollapsed((c) => !c)}>
        <span className="wf-exec-toggle">&#9660;</span>
        <span className="wf-exec-title">
          {isBatch ? `Batch Execution (${batchProgress.total} docs)` : 'Execution Log'}
        </span>

        {executionStatus && (
          <span className={`wf-exec-status-badge ${executionStatus}`}>
            {executionStatus}
          </span>
        )}

        <div className="wf-exec-header-spacer" />

        {overallDuration && (
          <span className="wf-exec-duration">{overallDuration}</span>
        )}

        <button
          className="wf-exec-close-btn"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          title="Close panel"
        >
          &times;
        </button>
      </div>

      {!collapsed && (
        <div className="wf-exec-body" ref={bodyRef}>
          {isBatch ? (
            <BatchSummaryView
              batchProgress={batchProgress}
              batchResults={batchResults || []}
              executionStatus={executionStatus}
            />
          ) : (
            <>
              {(!nodeLog || nodeLog.length === 0) && (
                <div className="wf-exec-empty">
                  {executionStatus === 'running' ? 'Waiting for first node...' : 'No log entries'}
                </div>
              )}

              {(nodeLog || []).map((entry, idx) => {
                const duration = computeDuration(entry.started_at, entry.completed_at);
                return (
                  <div key={`${entry.node_id}-${idx}`} className="wf-exec-log-entry">
                    <span className="wf-exec-log-time">{formatTime(entry.started_at)}</span>
                    <span className="wf-exec-log-label">{entry.label || entry.node_id}</span>
                    <span className="wf-exec-log-type">({entry.node_type})</span>
                    <span className="wf-exec-log-status">&mdash;</span>
                    <span className={`wf-exec-log-status ${entry.status}`}>{entry.status}</span>
                    {duration && <span className="wf-exec-log-duration">{duration}</span>}
                  </div>
                );
              })}

              {errorMessage && (
                <div className="wf-exec-error">{errorMessage}</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutionPanel;
